/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  DatalayerPopupContentTypes,
  DataLayerTrackEvents,
} from '@/datalayer/App.datalayer.constants';
import { getMakeAndModel } from '@/features/SDL/SDL.datalayer';
import SDLdetail from '@/features/SDL/SDLdetail/SDLdetail';
import ElasticAdapter from '@/features/shared/Adapters/ElasticAdapter';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import { useDataLayerSearchEvents } from '@/features/shared/Hooks/data-layer';
import { trackSearchClick } from '@/features/shared/Hooks/useElasticSearch';
import {
  MuiCircularProgress,
  MuiContainer,
  MuiTable,
  MuiTableBody,
  MuiTableCell,
  MuiTableContainer,
  MuiTableHead,
  MuiTableRow,
  MuiTableSortLabel,
} from '@/features/shared/material-ui';
import ModalContent from '@/features/shared/ModalContent';
import { ROOT_BASE_URL, Theme } from '@/utils/constants';
import { KeyboardArrowDown } from '@material-ui/icons';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import qs from 'query-string';
import React, {
  memo,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet-async';
import { useHistory, useLocation } from 'react-router-dom';
import { EnhancedTableProps, TableData } from './SDLpaginatedTable.interface';
import { getRowItem, wrapData } from './SDLpaginatedTable.mapper';
import useStyles from './SDLpaginatedTable.styles';

const adaptTitle = (title: string) => title.replace(/\((.+?)\)/g, '<small>($1)</small>');

function EnhancedTableHead(props: EnhancedTableProps) {
  const { headers, classes, order, orderBy, onRequestSort, theme } = props;
  const createSortHandler = (property: keyof TableData) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <MuiTableHead>
      <MuiTableRow className={classes.headerRow}>
        {headers.map((headCell: any) => (
          <MuiTableCell
            key={headCell.key}
            className={classes.headerCell}
            align="left"
            padding="default"
            size="medium"
            sortDirection={orderBy === headCell.key ? order : false}
          >
            <MuiTableSortLabel
              active={orderBy === headCell.key}
              direction={orderBy === headCell.key ? order : 'asc'}
              onClick={createSortHandler(headCell.key)}
              IconComponent={KeyboardArrowDown}
            >
              {/* eslint-disable-next-line react/no-danger */}
              <span dangerouslySetInnerHTML={{ __html: adaptTitle(headCell.value || '') }} />
              {orderBy === headCell.key ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </MuiTableSortLabel>
          </MuiTableCell>
        ))}
      </MuiTableRow>
    </MuiTableHead>
  );
}

const SDLpaginatedTable = ({
  theme = Theme.dark,
  headers = [],
  data = [],
  isLoading = false,
  labels,
  deviceType = '',
}: {
  theme?: ThemeType;
  headers?: any[];
  data?: any[];
  isLoading?: boolean;
  labels?: any[];
  deviceType?: string;
}) => {
  const classes = useStyles({});
  const reactHistory = useHistory();
  const location = useLocation();
  const getSearchID = React.useCallback(() => {
    const { id } = qs.parse(location.search);
    const value = Array.isArray(id) ? id[0] : id;
    return isEmpty(value) ? null : value;
  }, [location.search]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isOpen, setisOpen] = useState(false);
  const [detailContent, setDetailContent] = useState<any>({});
  const {
    queryParams,
    updateQueryParams,
    engine,
    requestID,
    forceUpdate = () => null,
    externalIDUpdate = () => null,
    searchKey = '',
  } = useContext(ElasticComponentContext);
  const [selectedDevice, setSelectedDevice] = useState(getSearchID());
  const tableRef = useRef<HTMLDivElement>(null);
  const { triggerSearchEventDataLayer } = useDataLayerSearchEvents();

  const handleRequestSort = (event: MouseEvent<unknown>, property: any) => {
    updateQueryParams('sortBy', property);
    updateQueryParams('sortType', queryParams.sortType === 'asc' ? 'desc' : 'asc');
    forceUpdate();
    triggerSearchEventDataLayer();
  };

  const loadModal = (content: any) => {
    setDetailContent(content);
    setisOpen(true);
    const variantName = (tableRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement)
      ?.dataset?.datalayerVariant;

    datalayerPush({
      compRef: tableRef,
      args: {
        event: 'clickResult',
        makeAndModel: getMakeAndModel(content),
        variantName,
      },
      event: 'gtm:custom:sdl',
    });
  };

  useEffect(() => {
    if (!selectedDevice) {
      return;
    }

    const deviceInfo = data.filter((choice: any) => choice.id.raw === getSearchID());
    if (isEmpty(deviceInfo)) {
      return;
    }

    loadModal(deviceInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, data]);

  // Update queryParams "id" by closing modal.
  const closeModal = React.useCallback(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: DataLayerTrackEvents.EXIT_POPUP,
        event_name: 'exit_popup',
        content_type: DatalayerPopupContentTypes.DEVICE_DETAIL,
        title: `${(labels as any)?.detail_device_details} - ${getMakeAndModel(detailContent)}`,
        sdl_category: queryParams?.category_type,
        sdl_detail_id: getSearchID(),
        make_model: getMakeAndModel(detailContent),
      },
    });
    updateQueryParams('id', '', true);
    setSelectedDevice(null);
    forceUpdate();
    setisOpen(false);
  }, [
    detailContent,
    forceUpdate,
    getSearchID,
    labels,
    queryParams?.category_type,
    updateQueryParams,
  ]);

  const toggleModal = () => {
    if (isOpen) {
      updateQueryParams('id', '', true);
      setSelectedDevice(null);
      forceUpdate();
    }
    setisOpen(!isOpen);
  };

  const findDeviceID = useCallback(
    (device: any) => {
      const deviceID = device.getAttribute('data-id');
      // Enforce URL with deviceId.
      setSelectedDevice(deviceID);
      reactHistory.push(`?${qs.stringify(ElasticAdapter.sanitizeParams({ id: deviceID }))}`, {});

      if (!deviceID) return;

      trackSearchClick(searchKey, queryParams.q, engine, deviceID, requestID);

      const deviceInfo = data.filter((choice: any) => choice.id.raw === deviceID);
      const dataLayer = {
        event: 'trackEvent-sdlViewDeviceDetail',
        event_name: 'sdl_view_device_detail',
        position: 'sdl',
        sdl_category: queryParams?.category_type,
        sdl_detail_id: deviceID,
        make_model: getMakeAndModel(deviceInfo),
      };
      TagManager.dataLayer({ dataLayer });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, queryParams.q, requestID],
  );

  useEffect(() => {
    const searchID = getSearchID();
    if (searchID !== selectedDevice) {
      updateQueryParams('id', searchID, true);
      setSelectedDevice(searchID);
      externalIDUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleClick = useCallback(
    (event: MouseEvent<unknown>, name: string) => {
      event.preventDefault();
      // SET BASIC SELECTED STATE.
      const selectedIndex = selected.indexOf(name);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }
      findDeviceID(event.target);
      setSelected(newSelected);
    },
    [findDeviceID, selected],
  );

  const setProductLink = (deviceID: string) => {
    const { pathname: url } = window.location;
    const query = { id: deviceID };

    return qs.stringifyUrl({ url, query });
  };

  const rootClasses = clsx(classes.root, `${theme}-theme`);

  const wrappedData = useMemo(
    () =>
      data &&
      wrapData({ data }).map((row: any, index, array) => {
        const labelId = `enhanced-table-checkbox-${index}`;
        return (
          <MuiTableRow
            hover
            onClick={(event: MouseEvent) => handleClick(event, `${headers[0].key}`)}
            tabIndex={-1}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={classes.bodyRow}
          >
            {headers.map((item, i) => {
              const rowItemLabel = getRowItem({ row, key: item.key, deviceType });
              const firstProps = i ? {} : { id: labelId, scope: 'row' };
              return (
                <MuiTableCell
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${labelId}-${i}`}
                  className={classes.bodyCell}
                  data-id={row.id}
                  size="small"
                  {...firstProps}
                >
                  {i === 1 && rowItemLabel ? (
                    <a href={setProductLink(row.id)} data-id={row.id} data-datalayer-ignore-link>
                      {rowItemLabel}
                    </a>
                  ) : (
                    rowItemLabel || '-'
                  )}
                </MuiTableCell>
              );
            })}
          </MuiTableRow>
        );
      }),
    [classes.bodyCell, classes.bodyRow, data, handleClick, headers, deviceType],
  );

  const contentOpen = isOpen && !isEmpty(detailContent);

  return (
    <MuiTableContainer className={rootClasses} ref={tableRef}>
      <MuiTable
        className={classes.table}
        aria-labelledby="tableTitle"
        size="medium"
        aria-label="enhanced table"
      >
        <EnhancedTableHead
          headers={headers}
          classes={classes}
          numSelected={selected.length}
          order={queryParams.sortType}
          orderBy={queryParams.sortBy}
          onRequestSort={handleRequestSort}
          rowCount={data.length}
        />
        <MuiTableBody>
          {(isLoading && (
            <MuiTableRow>
              <MuiTableCell colSpan={headers.length} className={classes.spinnerCell} align="center">
                <MuiCircularProgress size="4rem" />
              </MuiTableCell>
            </MuiTableRow>
          )) ||
            wrappedData}
        </MuiTableBody>
      </MuiTable>
      <ModalContent
        isOpen={contentOpen}
        toggleOpen={toggleModal}
        title="device-detail"
        description="device-detail-ID"
        className={classes.modal}
      >
        <MuiContainer>
          <SDLdetail
            content={detailContent}
            modalClose={closeModal}
            theme={theme}
            labels={labels}
          />
          {contentOpen && (
            <Helmet>
              <meta name="robots" content="index, follow" />
              <link
                rel="canonical"
                href={`${ROOT_BASE_URL}${setProductLink(selectedDevice || '')}`}
              />
            </Helmet>
          )}
        </MuiContainer>
      </ModalContent>
    </MuiTableContainer>
  );
};

export default memo(SDLpaginatedTable);
